import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../actions/userActions';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './Profile.css';
import FriendsList from './friendsList';
import { FaUser, FaEdit, FaSignOutAlt, FaStar, FaFire, FaTrophy } from 'react-icons/fa';
import PrivateChat from './PrivateChat';

const Profile = () => {
  const { id } = useParams();
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [streakInfo, setStreakInfo] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails);
  const { userInfo } = useSelector((state) => state.userLogin);
  const navigate = useNavigate();
  const isUserDetailsFetched = useRef(false);
  const [activeChat, setActiveChat] = useState(null);

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    } else if (!isUserDetailsFetched.current && userInfo.token) {
      dispatch(getUserDetails(id));
      isUserDetailsFetched.current = true;
    }
  }, [dispatch, navigate, userInfo, id]);

  useEffect(() => {
    // Check if profile data has calculated_stats from enhanced endpoint
    if (userDetails.user?.calculated_stats) {
      // Use real-time calculated stats from profile endpoint
      const stats = userDetails.user.calculated_stats;
      setQuestionsAnswered(stats.total_questions || 0);
      setCorrectAnswers(stats.correct_answers || 0);
      setIncorrectAnswers(stats.incorrect_answers || 0);
      console.log("Using calculated stats from profile:", stats);
    } else if (userDetails.user) {
      // Fallback to separate questions_answered endpoint if calculated_stats not available
      const fetchQuestionsAnswered = async () => {
        if (!userInfo?.token) return null;
    
        try {
          const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
          const { data } = await axios.get('https://api.andrewslearning.com/api/questions_answered', config);
          const correct = data.correct_responses || 0;
          const incorrect = data.incorrect_responses || 0;
          setCorrectAnswers(correct);
          setIncorrectAnswers(incorrect);
          setQuestionsAnswered(correct + incorrect);
          return data;
        } catch (error) {
          console.error('Error fetching questions answered:', error.message);
          return null;
        }
      };
  
      fetchQuestionsAnswered().then(data => {
        if (data) {
          console.log("Fetched questions data:", data);
        }
      });
    }
  }, [userInfo, userDetails.user]);

  useEffect(() => {
    // Fetch streak information
    const fetchStreakInfo = async () => {
      if (!userInfo?.token) return;
      
      try {
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.get('https://api.andrewslearning.com/api/streak/', config);
        setStreakInfo(data);
        console.log("Streak info:", data);
      } catch (error) {
        console.error('Error fetching streak info:', error.message);
      }
    };

    if (userInfo?.token) {
      fetchStreakInfo();
    }
  }, [userInfo]);

  useEffect(() => {
    // Fetch achievements
    const fetchAchievements = async () => {
      if (!userInfo?.token) return;
      
      try {
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.get('https://api.andrewslearning.com/api/achievements/', config);
        setAchievements(data);
        console.log("Achievements:", data);
      } catch (error) {
        console.error('Error fetching achievements:', error.message);
      }
    };

    if (userInfo?.token) {
      fetchAchievements();
    }
  }, [userInfo]);

  const handleCancelSubscription = async () => {
    if (!userInfo?.token) return;
    try {
      const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
      const response = await axios.post(
        'https://api.andrewslearning.com/api/cancel_subscription',
        { user_id: userInfo.id },
        config
      );

      alert(response.data?.detail ? 'Subscription canceled successfully.' : 'Failed to cancel subscription. Please try again.');
    } catch (error) {
      console.error('Error canceling subscription:', error.message);
      alert(`An error occurred: ${error.message}`);
    }
  };

  return (
    <div className="profile-page-container">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-pic-placeholder">
            {userDetails.user?.Propic ? (
              <img src={userDetails.user.Propic} alt="Profile" />
            ) : (
              <FaUser size={60} color="#3b82f6" />
            )}
          </div>
        </div>
        
        <div className="profile-info">
          <h2 className="username-display">{userDetails.user?.username || 'User'}</h2>
          
          <div className="profile-stats">
            <div className="stat-card">
              <div className="stat-label">Level</div>
              <div className="stat-value">
                {userDetails.user?.level_info?.name || userDetails.user?.level || 1}
                {userDetails.user?.level_info?.badge_icon && (
                  <i className={`${userDetails.user.level_info.badge_icon} ml-2`} 
                     style={{color: userDetails.user.level_info.badge_color}}></i>
                )}
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-label">XP</div>
              <div className="stat-value">{userDetails.user?.xp || 0}</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-label">Streak</div>
              <div className="stat-value">
                {streakInfo?.current_streak || userDetails.user?.streak || 0}
                {(streakInfo?.current_streak || userDetails.user?.streak || 0) > 0 && <FaFire className="ml-1" style={{color: '#ff6b35'}} />}
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-label">Questions</div>
              <div className="stat-value">{questionsAnswered}</div>
            </div>
          </div>

          {/* Level Progress Section */}
          {userDetails.user?.level_progress && (
            <div className="level-progress-section">
              <h3 className="section-title">
                <FaTrophy className="mr-2" />
                Level Progress
              </h3>
              
              <div className="level-info-card">
                <div className="current-level-info">
                  <div className="level-badge" style={{backgroundColor: userDetails.user.level_info?.badge_color || '#007bff'}}>
                    <i className={userDetails.user.level_info?.badge_icon || 'fas fa-star'}></i>
                    <span>Level {userDetails.user.level_progress.current_level}</span>
                  </div>
                  <div className="level-details">
                    <h4>{userDetails.user.level_info?.name || 'Level ' + (userDetails.user.level_progress.current_level || 1)}</h4>
                    <p>{userDetails.user.level_info?.description || 'Keep learning to progress!'}</p>
                  </div>
                </div>

                <div className="progress-container">
                  <div className="progress-info">
                    <span>Progress to Next Level</span>
                    <span>{Math.round(userDetails.user.level_progress.progress_percentage)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{
                        width: `${userDetails.user.level_progress.progress_percentage}%`,
                        backgroundColor: userDetails.user.level_info?.badge_color || '#007bff'
                      }}
                    ></div>
                  </div>
                  <div className="xp-info">
                    <span>{userDetails.user.level_progress.current_xp} XP</span>
                    {userDetails.user.level_progress.next_level_xp && (
                      <span>{userDetails.user.level_progress.xp_needed} XP to next level</span>
                    )}
                  </div>
                </div>

                {/* Unlocked Features */}
                {userDetails.user?.unlocked_features && userDetails.user.unlocked_features.length > 0 && (
                  <div className="unlocked-features">
                    <h5><FaStar className="mr-2" />Unlocked Features</h5>
                    <div className="features-list">
                      {userDetails.user.unlocked_features.map((feature, index) => (
                        <div key={index} className="feature-badge">
                          <i className="fas fa-check-circle mr-1"></i>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Daily Login Streak Section */}
          {streakInfo && (
            <div className="level-progress-section">
              <h3 className="section-title">
                <FaFire className="mr-2" style={{color: '#ff6b35'}} />
                Daily Login Streak
              </h3>
              
              <div className="level-info-card">
                <div className="current-level-info">
                  <div className="level-badge" style={{backgroundColor: '#ff6b35'}}>
                    <FaFire />
                    <span>{streakInfo.current_streak} days</span>
                  </div>
                  <div className="level-details">
                    <h4>
                      {streakInfo.logged_in_today ? "✅ Logged in today!" : "⏰ Login today to continue streak"}
                    </h4>
                    <p>
                      Longest streak: {streakInfo.longest_streak} days • 
                      XP Multiplier: {streakInfo.multiplier}x
                    </p>
                  </div>
                </div>

                {streakInfo.level_info?.daily_xp_bonus > 0 && (
                  <div className="unlocked-features">
                    <h5><FaStar className="mr-2" />Daily Rewards</h5>
                    <div className="features-list">
                      <div className="feature-badge">
                        <i className="fas fa-gift mr-1"></i>
                        {streakInfo.level_info.daily_xp_bonus} XP per login
                      </div>
                      {streakInfo.current_streak >= 7 && (
                        <div className="feature-badge">
                          <i className="fas fa-fire mr-1"></i>
                          {streakInfo.multiplier}x bonus active
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Achievements Section */}
          {achievements && (
            <div className="level-progress-section">
              <h3 className="section-title">
                <FaTrophy className="mr-2" />
                Achievements
              </h3>
              
              <div className="level-info-card">
                {/* Recent achievements */}
                {achievements.earned_achievements && achievements.earned_achievements.length > 0 && (
                  <div className="unlocked-features">
                    <h5><FaStar className="mr-2" />Earned Achievements ({achievements.earned_achievements.length})</h5>
                    <div className="features-list">
                      {achievements.earned_achievements.slice(0, 6).map((achievement, index) => (
                        <div key={index} className="feature-badge" style={{backgroundColor: achievement.badge_color}}>
                          <i className={`${achievement.badge_icon} mr-1`}></i>
                          {achievement.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available achievements */}
                {achievements.available_achievements && achievements.available_achievements.length > 0 && (
                  <div className="unlocked-features" style={{marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem'}}>
                    <h5><FaFire className="mr-2" />Next Goals ({achievements.available_achievements.length})</h5>
                    <div className="features-list">
                      {achievements.available_achievements.slice(0, 4).map((achievement, index) => (
                        <div key={index} className="feature-badge" style={{
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d',
                          border: `2px solid ${achievement.badge_color}`
                        }}>
                          <i className={`${achievement.badge_icon} mr-1`}></i>
                          {achievement.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newly earned notification */}
                {achievements.newly_earned && achievements.newly_earned.length > 0 && (
                  <div className="unlocked-features" style={{
                    marginTop: '1rem', 
                    borderTop: '1px solid #e2e8f0', 
                    paddingTop: '1rem',
                    backgroundColor: '#fff3cd',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #ffeaa7'
                  }}>
                    <h5><FaTrophy className="mr-2" style={{color: '#ffd700'}} />🎉 New Achievement!</h5>
                    <div className="features-list">
                      {achievements.newly_earned.map((achievement, index) => (
                        <div key={index} className="feature-badge" style={{
                          backgroundColor: achievement.badge_color,
                          animation: 'pulse 2s infinite'
                        }}>
                          <i className={`${achievement.badge_icon} mr-1`}></i>
                          {achievement.name} (+{achievement.xp_reward} XP)
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <p className="profile-email">
            <strong>Email</strong>
            {userDetails.user?.email || 'No email provided'}
          </p>
          
          <div className="profile-buttons">
            <button className="update-button">
              <FaEdit /> Update Profile
            </button>
            
            <button className="cancel-button" onClick={handleCancelSubscription}>
              <FaSignOutAlt /> Cancel Subscription
            </button>
          </div>
        </div>
      </div>

      {/* Friends List Column */}
      <div className="friends-list-container">
        <div className="friends-list-title">Friends</div>
        <FriendsList onChatSelect={setActiveChat} />
      </div>
      {activeChat && <PrivateChat activeChat={activeChat} />}
    </div>
  );
};

export default Profile;
